import React, { useEffect, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormDescription,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { DocumentGroupSelector } from "@/components/associations/DocumentGroupSelector";
import { DocumentGroup } from "@/models/DocumentGroup";
import { PricingTable } from "@/models/PricingTable";
import { Association } from "@/models/Association";
import { useQuery } from "@tanstack/react-query";
import { associationApi } from "@/services/api";
import { AssociationFormValues } from "@/types/association";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Check, ChevronsUpDown, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { extractDataArray } from "@/utils/apiUtils";
import { useAuth } from "@/contexts/AuthContext";

interface GeneralInformationTabProps {
  pricingTables: PricingTable[];
  onSelectDocumentGroup: (documentGroup: DocumentGroup) => void;
  onRemoveDocumentGroup: (groupId: number) => void;
  getPricingTableName: (id: number | null) => string;
  isEditing?: boolean;
  associationId?: number;
  managementCompanyId?: number;
}

export const GeneralInformationTab: React.FC<GeneralInformationTabProps> = ({
  pricingTables,
  onSelectDocumentGroup,
  onRemoveDocumentGroup,
  getPricingTableName,
  isEditing = false,
  associationId,
  managementCompanyId
}) => {
  const { control, watch, setValue, getValues } = useFormContext<AssociationFormValues>();
  const { currentUser } = useAuth();
  const [open, setOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [selectedParentName, setSelectedParentName] = useState<string>("Select parent association");
  
  const parentAssociationId = useWatch({
    control,
    name: "parent_association_id",
  });
  
  // Use the passed managementCompanyId for filtering, or fall back to currentUser's management company
  const filterManagementCompanyId = managementCompanyId ?? currentUser?.management_company_id;
  
  const { data: associationsResponse, isLoading: isAssociationsLoading } = useQuery({
    queryKey: ["associations", { limit: 1000, search: searchValue, management_company_id: filterManagementCompanyId }],
    queryFn: () => associationApi.list({ 
      queryKey: ["associations", { limit: 1000, search: searchValue, management_company_id: filterManagementCompanyId }] 
    }),
    enabled: (open || !!parentAssociationId) && !!filterManagementCompanyId,
  });

  const associations = React.useMemo(() => {
    const dataArray = extractDataArray(associationsResponse);
    return Array.isArray(dataArray) ? dataArray : [];
  }, [associationsResponse]);
  
  const parentOptions = React.useMemo(() => {
    return associations.filter(assoc => !associationId || assoc.id !== associationId);
  }, [associations, associationId]);

  const filteredOptions = React.useMemo(() => {
    const lowerSearch = searchValue.toLowerCase();
    return parentOptions
      .filter(
        association => association.name?.toLowerCase().includes(lowerSearch)
      )
      .sort((a, b) => a.name.localeCompare(b.name)); // Sort alphabetically
  }, [parentOptions, searchValue]);

  const associationName = useWatch({
    control,
    name: "name",
  });

  const externalId = useWatch({
    control,
    name: "external_id",
  });

  const handleSearchChange = (value: string) => {
    setSearchValue(value);
  };
  
  // Effect to update the external ID suggestion based on association name
  useEffect(() => {
    if (!isEditing && associationName && (!externalId || externalId.trim() === '')) {
      const words = associationName.split(/\s+/);
      const capitalLetterWords = words.filter(word => /^[A-Z]/.test(word));
      
      if (capitalLetterWords.length > 0) {
        const suggestedExternalId = capitalLetterWords.join('').toUpperCase();
        setValue('external_id', suggestedExternalId);
      }
    }
  }, [associationName, setValue, externalId, isEditing]);

  // Effect to set the selected parent association name when data is loaded
  useEffect(() => {
    if (parentAssociationId && parentOptions.length > 0) {
      const parent = parentOptions.find(assoc => assoc.id === parentAssociationId);
      if (parent) {
        setSelectedParentName(parent.name);
      }
    } else if (parentAssociationId === null) {
      setSelectedParentName("None");
    }
  }, [parentAssociationId, parentOptions]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>General Information</CardTitle>
        <CardDescription>
          Basic information about the association
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <FormField
          control={control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Association Name</FormLabel>
              <FormControl>
                <Input {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <FormField
          control={control}
          name="association_type"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Association Type</FormLabel>
              <Select
                value={field.value}
                onValueChange={field.onChange}
              >
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select an association type" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="Condominium">Condominium</SelectItem>
                  <SelectItem value="HOA">Homeowners Association (HOA)</SelectItem>
                  <SelectItem value="COOP">Cooperative</SelectItem>
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <FormField
          control={control}
          name="accepts_external_applicants"
          render={({ field }) => (
            <FormItem className="flex flex-row items-start justify-between rounded-lg border p-4">
              <div className="space-y-0.5 pr-4">
                <FormLabel className="text-base">Accept external applicants</FormLabel>
                <FormDescription>
                  When on, co-applicants receive their own signing link by email
                  and sign for themselves. When off, the person filling the
                  application signs on behalf of every applicant.
                  <br />
                  <span className="text-xs text-muted-foreground">
                    Only affects orders created after this is enabled — orders
                    already in progress are unchanged.
                  </span>
                </FormDescription>
              </div>
              <FormControl>
                <Switch
                  checked={field.value === true}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
            </FormItem>
          )}
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            control={control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Email</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <FormField
            control={control}
            name="phone"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Phone</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={control}
          name="external_id"
          render={({ field }) => (
            <FormItem>
              <FormLabel>External ID *</FormLabel>
              <FormControl>
                <Input placeholder="External ID" {...field} value={field.value || ''} required />
              </FormControl>
              <FormDescription>
                {isEditing 
                  ? "Required unique identifier within your management company" 
                  : "Required unique identifier within your management company (auto-suggested based on name)"}
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            control={control}
            name="ein"
            render={({ field }) => (
              <FormItem>
                <FormLabel>EIN (Tax ID)</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <FormField
            control={control}
            name="tax_classification"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Tax Classification</FormLabel>
                <Select
                  value={field.value ?? ""}
                  onValueChange={(value) => field.onChange(value || null)}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select tax classification" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="LLC – Partnership">LLC – Partnership</SelectItem>
                    <SelectItem value="LLC – C Corporation">LLC – C Corporation</SelectItem>
                    <SelectItem value="LLC – S Corporation">LLC – S Corporation</SelectItem>
                    <SelectItem value="C Corporation">C Corporation</SelectItem>
                    <SelectItem value="S Corporation">S Corporation</SelectItem>
                    <SelectItem value="Individual or Single LLC">Individual or Single LLC</SelectItem>
                    <SelectItem value="Partnership">Partnership</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        
        <FormField
          control={control}
          name="pricing_table_id"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Pricing Table</FormLabel>
              <Select
                value={field.value?.toString() ?? "none"}
                onValueChange={(value) => field.onChange(value === "none" ? null : parseInt(value))}
              >
                <FormControl>
                  <SelectTrigger>
                    <SelectValue>
                      {field.value ? getPricingTableName(field.value) : "None (Use Default)"}
                    </SelectValue>
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="none">None (Use Default)</SelectItem>
                  {pricingTables.map((table: PricingTable) => (
                    <SelectItem key={table.id} value={table.id.toString()}>
                      {table.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormDescription>
                Select a specific pricing table for this association or use the default pricing
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="space-y-2 pt-4">
          <Label>Document Groups</Label>
          <DocumentGroupSelector
            selectedGroups={watch("document_groups") || []}
            onSelectGroup={onSelectDocumentGroup}
            onRemoveGroup={onRemoveDocumentGroup}
            managementCompanyId={filterManagementCompanyId}
            associationId={associationId}
          />
          <FormDescription>
            Document groups determine what documents are available for this association
          </FormDescription>
        </div>

        <FormField
          control={control}
          name="parent_association_id"
          render={({ field }) => (
            <FormItem className="flex flex-col">
              <FormLabel>Parent Association</FormLabel>
              <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                  <FormControl>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={open}
                      className="w-full justify-between"
                    >
                      {selectedParentName}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </FormControl>
                </PopoverTrigger>
                <PopoverContent className="w-full p-0" align="start">
                  <Command>
                    <CommandInput 
                      placeholder="Search associations..." 
                      value={searchValue}
                      onValueChange={handleSearchChange}
                    />
                    <CommandList>
                      {isAssociationsLoading ? (
                        <div className="py-6 text-center flex items-center justify-center">
                          <Loader2 className="h-4 w-4 animate-spin mr-2" />
                          <span>Loading...</span>
                        </div>
                      ) : filteredOptions.length === 0 && !searchValue ? (
                        <CommandEmpty>No association found.</CommandEmpty>
                      ) : (
                        <CommandGroup>
                          <CommandItem
                            value="none"
                            onSelect={() => {
                              field.onChange(null);
                              setSelectedParentName("None");
                              setOpen(false);
                            }}
                          >
                            <Check
                              className={cn(
                                "mr-2 h-4 w-4",
                                !field.value ? "opacity-100" : "opacity-0"
                              )}
                            />
                            None
                          </CommandItem>
                          {filteredOptions.map((association) => (
                            <CommandItem
                              key={association.id}
                              value={association.name || ""}
                              onSelect={() => {
                                field.onChange(association.id);
                                setSelectedParentName(association.name);
                                setOpen(false);
                              }}
                            >
                              <Check
                                className={cn(
                                  "mr-2 h-4 w-4",
                                  field.value === association.id
                                    ? "opacity-100"
                                    : "opacity-0"
                                )}
                              />
                              {association.name}
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      )}
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              <FormDescription>
                Select a parent association to inherit its documents
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
      </CardContent>
    </Card>
  );
};
